import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LogOut,
  Radar,
  Settings as SettingsIcon,
  MoreHorizontal,
  Trash2,
  Zap,
  CheckCircle2,
  Radio,
  Globe,
  Lock,
} from "lucide-react";
import SettingsDialog from "@/components/SettingsDialog";
import ActiveNicknamesDialog from "@/components/ActiveNicknamesDialog";
import HelpDialog from "@/components/HelpDialog";
import { SimpleDialog } from "@/components/ui/simple-dialog";

export default function Header({
  totalCards = 0,
  readyCount = 0,
  onBulkScope,
  onUseSet,
  onGetBoardCounts,
  mode,
  onModeToggle,
  userNickname,
  soundEnabled,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [nicknamesOpen, setNicknamesOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [onlineCount, setOnlineCount] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [useSetConfirmOpen, setUseSetConfirmOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="bulk-actions-trigger"]') && 
          !e.target.closest('[data-testid="bulk-actions-menu"]')) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [menuOpen]);

  // Send a heartbeat so the server knows this session is still active.
  // Fires immediately on mount, then every 2 minutes.
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        await api.post("/auth/heartbeat");
      } catch {
        // silently ignore — heartbeat is best-effort
      }
    };
    sendHeartbeat();
    const id = setInterval(sendHeartbeat, 2 * 60_000);
    return () => clearInterval(id);
  }, []);

  // Periodically fetch online count for the badge
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/users/active-nicknames");
        setOnlineCount(Array.isArray(data) ? data.length : null);
      } catch {
        // silently ignore
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30_000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleUseSet = () => {
    const counts = typeof onGetBoardCounts === "function" ? onGetBoardCounts() : {};
    const allZero = Object.values(counts).every((v) => (v || 0) === 0);
    if (allZero) return; // button should be disabled, but guard here too
    const hasZero = Object.values(counts).some((v) => (v || 0) === 0);
    if (hasZero) {
      setUseSetConfirmOpen(true);
    } else {
      onUseSet?.();
    }
  };

  return (
    <header className="border-b border-orange-500/15 bg-[#0B0E14]/80 backdrop-blur-xl sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border-thin border-orange-500/50 flex items-center justify-center bg-orange-500/5">
            <Radar className="w-4 h-4 text-orange-400" />
          </div>
          <div>
            <div className="font-display font-black text-lg tracking-tight leading-none text-slate-300">
              EXEC<span className="text-orange-400">/</span>HANGAR
            </div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500 mt-0.5">
              Card Cooldown Tracker
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end" data-testid="header-user-menu">
          <div className="text-right hidden xl:block">
            <div className="text-xs text-slate-400 tracking-widest uppercase">Operator</div>
            <div className="text-sm font-medium text-slate-200" data-testid="header-username">
              {userNickname ?? user?.email}
            </div>
          </div>

          {mode != null && onModeToggle && (
            <button
              onClick={() => onModeToggle(mode === "group" ? "private" : "group")}
              data-testid="mode-toggle"
              title={mode === "group" ? "Switch to Private mode" : "Switch to Group mode"}
              aria-label={mode === "group" ? "Switch to Private mode" : "Switch to Group mode"}
              className={
                mode === "private"
                  ? "flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border-thin border-emerald-500/60 text-emerald-400 hover:border-emerald-400 hover:text-emerald-300 transition-colors text-[11px] tracking-widest uppercase"
                  : "flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border-thin border-orange-500/60 text-orange-400 hover:border-orange-400 hover:text-orange-300 transition-colors text-[11px] tracking-widest uppercase"
              }
            >
              {mode === "private" ? (
                <Lock className="w-3.5 h-3.5" />
              ) : (
                <Globe className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">{mode === "private" ? "Private" : "Group"}</span>
            </button>
          )}

          <button
            onClick={() => setNicknamesOpen(true)}
            data-testid="open-who-is-online"
            aria-label="Who's online"
            title="Who's Online"
            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border-thin border-slate-700 text-slate-300 hover:border-orange-500 hover:text-orange-400 transition-colors text-[11px] tracking-widest uppercase"
          >
            <Radio className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Online</span>
            {onlineCount !== null && (
              <span className="ml-0.5 text-xs font-bold text-emerald-400 font-mono">{onlineCount}</span>
            )}
          </button>

          {onBulkScope && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                data-testid="bulk-actions-trigger"
                title="Bulk actions"
                aria-label="Bulk actions"
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border-thin border-slate-700 text-slate-300 hover:border-orange-500 hover:text-orange-400 transition-colors text-[11px] tracking-widest uppercase"
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Bulk</span>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 mt-1 bg-[#0B0E14] border-thin border-orange-500/30 rounded-none text-white min-w-[220px] z-50"
                  data-testid="bulk-actions-menu"
                >
                  <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500 px-3 py-2 border-b border-orange-500/10">
                    Bulk Actions
                  </div>
                  {onUseSet && (() => {
                    const counts = typeof onGetBoardCounts === "function" ? onGetBoardCounts() : {};
                    const allZero = Object.values(counts).every((v) => (v || 0) === 0);
                    return (
                      <button
                        onClick={() => {
                          handleUseSet();
                          setMenuOpen(false);
                        }}
                        disabled={allZero}
                        className="w-full text-left text-xs tracking-widest uppercase cursor-pointer px-3 py-2 hover:bg-emerald-500/10 hover:text-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        data-testid="bulk-use-set"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Use Set
                      </button>
                    );
                  })()}
                  <button
                    onClick={() => {
                      onBulkScope("ready");
                      setMenuOpen(false);
                    }}
                    disabled={readyCount === 0}
                    className="w-full text-left text-xs tracking-widest uppercase cursor-pointer px-3 py-2 hover:bg-emerald-500/10 hover:text-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="bulk-clear-ready"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    Clear Ready ({readyCount})
                  </button>
                  <button
                    onClick={() => {
                      onBulkScope("all");
                      setMenuOpen(false);
                    }}
                    disabled={totalCards === 0}
                    className="w-full text-left text-xs tracking-widest uppercase cursor-pointer px-3 py-2 hover:bg-orange-500/10 hover:text-orange-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="bulk-clear-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Clear All ({totalCards})
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => setHelpOpen(true)}
            data-testid="open-help"
            aria-label="Open help"
            title="Help"
            className="flex items-center justify-center w-9 h-9 border-thin border-slate-700 text-slate-300 hover:border-orange-500 hover:text-orange-400 transition-colors"
          >
            <span style={{ fontFamily: "'Roboto Mono', monospace", fontWeight: 300, fontSize: "1.5rem", lineHeight: 1 }}>?</span>
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            data-testid="open-settings"
            aria-label="Open settings"
            title="Settings"
            className="flex items-center justify-center w-9 h-9 border-thin border-slate-700 text-slate-300 hover:border-orange-500 hover:text-orange-400 transition-colors"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title="Sign out"
            aria-label="Sign out"
            className="flex items-center justify-center w-9 h-9 border-thin border-slate-700 text-slate-300 hover:border-orange-500 hover:text-orange-400 transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <HelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        mode={mode}
      />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onNext={() => { setSettingsOpen(false); setNicknamesOpen(true); }}
        mode={mode}
      />
      <ActiveNicknamesDialog
        open={nicknamesOpen}
        onOpenChange={setNicknamesOpen}
        onPrevious={() => { setNicknamesOpen(false); setSettingsOpen(true); }}
        mode={mode}
      />

      <SimpleDialog open={useSetConfirmOpen} onOpenChange={setUseSetConfirmOpen} className="max-w-sm">
        <div className={`px-4 py-4 border ${mode === "private" ? "bg-[#0B0E14]/95 border-emerald-500/30" : "bg-[#0B0E14]/95 border-orange-500/30"}`}>
          <div className={`text-[11px] tracking-[0.3em] uppercase mb-2 ${mode === "private" ? "text-emerald-400" : "text-orange-400"}`}>
            Incomplete Set
          </div>
          <p className="text-sm text-slate-300 leading-relaxed mb-3">
            One or more boards have 0 cards. A full set is unavailable. Decrement only the boards with cards?
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setUseSetConfirmOpen(false)}
              className="px-4 py-2 text-xs tracking-widest uppercase border border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setUseSetConfirmOpen(false);
                onUseSet?.();
              }}
              className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${
                mode === "private"
                  ? "border-emerald-500/60 text-emerald-400 hover:border-emerald-400 hover:text-emerald-300"
                  : "border-orange-500/60 text-orange-400 hover:border-orange-400 hover:text-orange-300"
              }`}
            >
              Proceed
            </button>
          </div>
        </div>
      </SimpleDialog>
    </header>
  );
}
